import { FC } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSnackbar, VariantType } from "notistack";

import { FormWrapper } from "@/components/Forms/components/MonolithFormComponents";
import { usePermissions } from "@/hooks/usePermissions";
import { safeParseJSON } from "@/utils/File-parsers/safeParseJSON";

import EvidenceAPI from "@/api/evidence";
import LoggingAPI from "@/api/logging";
import CustomFieldsApi from "@/api/custom-fields";

import { Contact } from "@/Features";
import { CustomAttribute } from "@/types";
import { CUSTOM_FIELD_CATEGORIES } from "@/pages/Settings/CustomFieldSettings/utils/customFieldConstants";
import { CustomField } from "@/Features/CustomFields/custom-fields.types";

import DefaultEvidenceForm from "./DefaultEvidenceForm";
import {
  EvidenceFormData,
  EditEvidenceFormData,
  Evidence,
} from "../evidence.types";
import { SignatureFieldRef } from "@/Features";

interface HandleEditFinished {
  evidenceInfo: Evidence;
  newAttributes: CustomAttribute[];
  updateData: Partial<Evidence>;
}

interface EditEvidenceFormProps {
  width?: number | undefined;
  defaultEvidenceInfo: Evidence;
  onSubmit: (data: HandleEditFinished) => void;
  onCancel: () => void;
}

const EditEvidenceForm: FC<EditEvidenceFormProps> = ({
  width,
  defaultEvidenceInfo,
  onSubmit,
  onCancel,
}) => {
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  // Fetch custom fields for processing custom attributes
  const { data: customFields } = useQuery({
    queryKey: [
      "custom_fields",
      "list",
      { category: CUSTOM_FIELD_CATEGORIES.EVIDENCE },
    ],
    queryFn: () =>
      CustomFieldsApi.getCustomFields({
        category: CUSTOM_FIELD_CATEGORIES.EVIDENCE,
      }),
  });

  const handleSubmit = async (
    data: EvidenceFormData,
    diffData?: EditEvidenceFormData,
    signatures?: {
      receivedBySig: SignatureFieldRef;
      receivedFromSig: SignatureFieldRef;
    }
  ) => {
    try {
      if (defaultEvidenceInfo.evidence_id && diffData) {
        const updateData: Record<string, unknown> = { ...diffData };

        // Handle contact updates (same logic as original)
        const contact = updateData.contact as Contact | null | undefined;
        if (contact && contact.client_id === null) {
          delete updateData.contact;
        } else if (contact) {
          updateData.contact_id = contact.client_id;
          delete updateData.contact;
        }

        // Handle custom attributes (same logic as original EditEvidenceForm)
        const customFieldUpdateData: Record<string, unknown> = {};
        const previousDataForLogging: Record<string, unknown> = {};
        const updatedCustomFieldValuesForLogging: Record<string, unknown> = {};

        // Find changed custom field values
        Object.keys(updateData).forEach((key) => {
          if (key.startsWith("custom_attribute_")) {
            customFieldUpdateData[key] = updateData[key];
          }
        });

        // Process custom attributes
        const customAttributes: Partial<CustomAttribute>[] = [];
        for (const key of Object.keys(customFieldUpdateData)) {
          const field_id = parseInt(key.replace("custom_attribute_", ""));
          if (!field_id) continue;

          // Find the custom field definition from the API data
          const customField = customFields?.find(
            (field: CustomField) => field.field_id === field_id
          );

          const currentAttr = defaultEvidenceInfo.custom_attributes?.find(
            (attribute) => {
              return attribute.field_id === field_id;
            }
          );

          // evidence has attribute & it hasn't changed
          if (currentAttr && currentAttr.value === customFieldUpdateData[key])
            continue;

          if (customField) {
            previousDataForLogging[`${customField.field_name}`] = safeParseJSON(
              currentAttr?.value || ""
            );

            updatedCustomFieldValuesForLogging[`${customField.field_name}`] =
              safeParseJSON(customFieldUpdateData[key]);

            customAttributes.push({
              attribute_id: currentAttr ? currentAttr.attribute_id : undefined,
              field_id: field_id,
              field_name: customField.field_name,
              item_id: defaultEvidenceInfo.evidence_id,
              value: customFieldUpdateData[key] as
                | string
                | number
                | string[]
                | undefined,
              editor_type: customField.editor_type,
            });
          }
        }

        // Add custom attributes to update data if there are changes
        if (customAttributes.length > 0) {
          updateData.custom_attributes = customAttributes;
        }

        // Remove custom field keys from main update data
        Object.keys(updateData).forEach((key) => {
          if (key.startsWith("custom_attribute_")) {
            delete updateData[key];
          }
        });

        // Only update if there are changes
        if (Object.keys(updateData).length > 0) {
          await EvidenceAPI.updateEvidence(
            defaultEvidenceInfo.evidence_id,
            updateData
          );

          // Handle custom attributes in response (same as EditEvidenceCustomFields)
          if (updateData.custom_attributes) {
            const oldAttrs = [...(defaultEvidenceInfo.custom_attributes || [])];
            const newAttrs = [
              ...(updateData.custom_attributes as Partial<CustomAttribute>[]),
            ];

            // Create a map of old attributes for faster lookup
            const oldAttrMap = new Map(
              oldAttrs.map((attr) => [attr.field_id, attr])
            );

            // Update or add new attributes
            const updatedAttrs = oldAttrs.map((oldAttr) => {
              const newAttr = newAttrs.find(
                (a: Partial<CustomAttribute>) => a.field_id === oldAttr.field_id
              );
              if (newAttr) {
                return {
                  ...oldAttr,
                  value:
                    newAttr.editor_type === "tagBox"
                      ? JSON.stringify(newAttr.value)
                      : newAttr.value,
                };
              }
              return oldAttr;
            });

            // Add any completely new attributes
            newAttrs.forEach((newAttr: Partial<CustomAttribute>) => {
              if (newAttr.field_id && !oldAttrMap.has(newAttr.field_id)) {
                updatedAttrs.push({
                  field_id: newAttr.field_id,
                  value:
                    newAttr.editor_type === "tagBox"
                      ? JSON.stringify(newAttr.value)
                      : newAttr.value,
                  attribute_id: 0,
                  item_id: defaultEvidenceInfo.evidence_id,
                  field_name: newAttr.field_name,
                  editor_type: newAttr.editor_type,
                  enabled: 1,
                  sort_value: 0,
                } as unknown as CustomAttribute);
              }
            });

            updateData.custom_attributes = updatedAttrs;
          }

          const editFinishedData: HandleEditFinished = {
            evidenceInfo: defaultEvidenceInfo,
            newAttributes: [],
            updateData: updateData as Partial<Evidence>,
          };

          onSubmit?.(editFinishedData);

          enqueueSnackbar(
            `Evidence ${defaultEvidenceInfo.evidence_number} updated`,
            { variant: "success" as VariantType }
          );

          // Enhanced logging that includes custom field changes
          let logMessage = `Edited Evidence: ${defaultEvidenceInfo.evidence_number}`;
          if (Object.keys(previousDataForLogging).length > 0) {
            const formatData = (data: Record<string, unknown>) => {
              return Object.entries(data)
                .map(([key, value]) => {
                  key = key
                    .split("_")
                    .map(
                      (word) =>
                        word.charAt(0).toUpperCase() +
                        word.slice(1).toLowerCase()
                    )
                    .join(" ");
                  if (value === null) {
                    value = "";
                  }
                  return `${key}: ${value}`;
                })
                .join("\n");
            };

            logMessage += `\n\nCustom Fields Changed:\nPrevious:\n${formatData(
              previousDataForLogging
            )}\n\nNew:\n${formatData(updatedCustomFieldValuesForLogging)}`;
          }

          LoggingAPI.createLog({
            user_id: currentUser.user_id,
            message: logMessage,
            event: "evidence:edit",
            object_type: "evidence",
          });
        } else {
          enqueueSnackbar(`No new data to update.`, {
            variant: "warning" as VariantType,
          });
          onCancel();
        }
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        `An error occurred while editing evidence. Please try again.`,
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <FormWrapper width={width}>
      <DefaultEvidenceForm
        formTitle={"Edit Evidence"}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        defaultFormData={
          {
            ...defaultEvidenceInfo,
            linked_contact: defaultEvidenceInfo.linked_contact?.client_id
              ? {
                  value: defaultEvidenceInfo.linked_contact.client_id,
                  label: defaultEvidenceInfo.linked_contact.name,
                  data: defaultEvidenceInfo.linked_contact,
                }
              : undefined,
            capacity_unit: defaultEvidenceInfo.capacity_unit || "GB",
          } as EvidenceFormData
        }
        includeCaseField={false}
        includeIntakeDetails={false}
        includeCustomFields={true}
        allowSmartPaste={false}
        buttonProps={{ submitText: "Save Changes" }}
      />
    </FormWrapper>
  );
};

export default EditEvidenceForm;
